package screens.auth

import androidx.compose.runtime.*
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import com.example.readingnotes.resources.Strings
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet

@Composable
fun EmailLoginScreen(
    onLoginSuccess: () -> Unit,
    onNavigateToLogin: () -> Unit,
    authViewModel: AuthViewModel,
) {
    val state by authViewModel.state.collectAsState()
    var emailValidationError by remember { mutableStateOf<String?>(null) }
    var emailValid by remember { mutableStateOf<Boolean?>(null) }
    var codeValid by remember { mutableStateOf<Boolean?>(null) }
    var showSuccessMessage by remember { mutableStateOf(false) }
    var countdown by remember { mutableIntStateOf(0) }

    // Countdown timer for resend button
    LaunchedEffect(countdown) {
        if (countdown > 0) {
            delay(1000)
            countdown--
        }
    }

    // Navigate on successful login
    LaunchedEffect(state.isLoggedIn) {
        if (state.isLoggedIn) {
            delay(500)
            onLoginSuccess()
        }
    }

    // Show success message when code is sent
    LaunchedEffect(state.verificationCodeSent) {
        if (state.verificationCodeSent && !showSuccessMessage) {
            showSuccessMessage = true
            countdown = 60
        }
    }

    fun validateEmail(email: String): Boolean {
        val emailRegex = "^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$"
        return email.matches(Regex(emailRegex))
    }

    fun validateCode(code: String): Boolean {
        return code.length == 6 && code.all { it.isDigit() }
    }

    // Real-time validation on email change
    LaunchedEffect(state.emailInput) {
        if (state.emailInput.isNotEmpty()) {
            emailValid = validateEmail(state.emailInput)
        } else {
            emailValid = null
        }
    }

    // Real-time validation on code change
    LaunchedEffect(state.verificationCodeInput) {
        if (state.verificationCodeSent && state.verificationCodeInput.isNotEmpty()) {
            codeValid = validateCode(state.verificationCodeInput)
        } else {
            codeValid = null
        }
    }

    fun handleSendCode() {
        emailValidationError = null
        val email = state.emailInput.trim()

        when {
            email.isBlank() -> emailValidationError = Strings.Auth.emailRequired
            !validateEmail(email) -> emailValidationError = Strings.Auth.emailInvalid
            else -> {
                showSuccessMessage = false
                authViewModel.onEvent(AuthEvent.OnSendVerificationCode)
            }
        }
    }

    fun handleVerify() {
        emailValidationError = null
        val email = state.emailInput.trim()
        val code = state.verificationCodeInput.trim()

        when {
            email.isBlank() -> emailValidationError = Strings.Auth.emailRequired
            !validateEmail(email) -> emailValidationError = Strings.Auth.emailInvalid
            code.isBlank() -> emailValidationError = Strings.Auth.codeRequired
            else -> authViewModel.onEvent(AuthEvent.OnVerifyEmailSubmit)
        }
    }

    fun handleLogin() {
        emailValidationError = null
        val email = state.emailInput.trim()
        val code = state.verificationCodeInput.trim()

        when {
            email.isBlank() -> emailValidationError = Strings.Auth.emailRequired
            !validateEmail(email) -> emailValidationError = Strings.Auth.emailInvalid
            code.isBlank() -> emailValidationError = Strings.Auth.codeRequired
            else -> authViewModel.onEvent(AuthEvent.OnLoginWithEmailSubmit)
        }
    }

    Div({ classes(AppStyleSheet.loginContainer) }) {
        Button({
            classes(AppStyleSheet.backButton)
            onClick { onNavigateToLogin() }
        }) { Text("← 戻る") }

        Div({ classes(AppStyleSheet.loginForm) }) {
            H2({ classes(AppStyleSheet.loginTitle) }) {
                Text(if (state.verificationCodeSent) Strings.Auth.verificationCode else Strings.Auth.emailLogin)
            }

            P({
                style {
                    property("color", "#666")
                    property("margin-bottom", "20px")
                    property("font-size", "14px")
                }
            }) {
                Text(
                    if (state.verificationCodeSent) {
                        Strings.Auth.emailVerifyDescription
                    } else {
                        Strings.Auth.emailLoginDescription
                    },
                )
            }

            // Success message
            if (showSuccessMessage) {
                Div({
                    style {
                        property("background-color", "#d4edda")
                        property("border", "1px solid #c3e6cb")
                        property("color", "#155724")
                        property("padding", "12px")
                        property("border-radius", "4px")
                        property("margin-bottom", "16px")
                    }
                }) {
                    Text(Strings.Auth.codeSentSuccess)
                }
            }

            // Email input
            Div({ classes(AppStyleSheet.formGroup) }) {
                val emailLabelClasses = buildList {
                    if (emailValid == true) add(AppStyleSheet.validationSuccess)
                    else if (emailValid == false) add(AppStyleSheet.validationError)
                }
                Div({ classes(*emailLabelClasses.toTypedArray()) }) {
                    Label("email-input") {
                        Text(Strings.Auth.email)
                    }
                }

                val emailInputClasses = buildList {
                    add(AppStyleSheet.textInput)
                    if (emailValid == true) add(AppStyleSheet.inputValid)
                    else if (emailValid == false) add(AppStyleSheet.inputInvalid)
                }
                Input(type = InputType.Email) {
                    id("email-input")
                    classes(*emailInputClasses.toTypedArray())
                    value(state.emailInput)
                    placeholder(Strings.Auth.emailPlaceholder)
                    attr("autocomplete", "email")
                    attr("required", "true")
                    attr("aria-invalid", if (emailValid == false) "true" else "false")
                    attr("aria-describedby", if (emailValid == false) "email-error" else "email-hint")
                    onInput { authViewModel.onEvent(AuthEvent.OnEmailChanged(it.value)) }
                    if (state.isLoading) {
                        disabled()
                    }
                }

                // Real-time validation message
                if (emailValid == false) {
                    P({
                        id("email-error")
                        classes(AppStyleSheet.validationMessage, AppStyleSheet.validationError)
                        attr("role", "alert")
                        attr("aria-live", "polite")
                    }) {
                        Text(Strings.Auth.emailInvalid)
                    }
                } else if (emailValid == true) {
                    P({
                        id("email-hint")
                        classes(AppStyleSheet.validationMessage, AppStyleSheet.validationSuccess)
                        attr("aria-live", "polite")
                    }) {
                        Text(Strings.Auth.validEmail)
                    }
                }
            }

            // Verification code input (shown after code is sent)
            if (state.verificationCodeSent) {
                Div({ classes(AppStyleSheet.formGroup) }) {
                    val codeLabelClasses = buildList {
                        if (codeValid == true) add(AppStyleSheet.validationSuccess)
                        else if (codeValid == false) add(AppStyleSheet.validationError)
                    }
                    Div({ classes(*codeLabelClasses.toTypedArray()) }) {
                        Label("code-input") {
                            Text(Strings.Auth.verificationCode)
                        }
                    }

                    val codeInputClasses = buildList {
                        add(AppStyleSheet.textInput)
                        if (codeValid == true) add(AppStyleSheet.inputValid)
                        else if (codeValid == false) add(AppStyleSheet.inputInvalid)
                    }
                    Input(type = InputType.Text) {
                        id("code-input")
                        classes(*codeInputClasses.toTypedArray())
                        value(state.verificationCodeInput)
                        placeholder(Strings.Auth.codePlaceholder)
                        attr("maxlength", "6")
                        attr("pattern", "[0-9]{6}")
                        attr("inputmode", "numeric")
                        attr("autocomplete", "one-time-code")
                        attr("required", "true")
                        attr("aria-invalid", if (codeValid == false) "true" else "false")
                        attr("aria-describedby", "code-hint")
                        onInput { authViewModel.onEvent(AuthEvent.OnVerificationCodeChanged(it.value)) }
                        if (state.isLoading) {
                            disabled()
                        }
                    }

                    // Code validation message and hint
                    P({
                        id("code-hint")
                        classes(AppStyleSheet.inputHint)
                        attr("aria-live", "polite")
                    }) {
                        when {
                            codeValid == true -> Text(Strings.Auth.validCode)
                            codeValid == false -> Text(Strings.Auth.codeExactLength)
                            else -> Text(Strings.Auth.codeExpiryHint)
                        }
                    }
                }
            }

            // Error messages
            if (emailValidationError != null) {
                Div({
                    classes(AppStyleSheet.formError)
                    attr("role", "alert")
                    attr("aria-live", "assertive")
                }) { Text(emailValidationError!!) }
            }
            if (state.errorMessage != null) {
                Div({
                    classes(AppStyleSheet.formError)
                    attr("role", "alert")
                    attr("aria-live", "assertive")
                }) { Text(state.errorMessage!!) }
            }

            // Action buttons
            Div({ classes(AppStyleSheet.loginButtonGroup) }) {
                if (!state.verificationCodeSent) {
                    // Send code button
                    Button({
                        classes(AppStyleSheet.submitButton)
                        onClick { handleSendCode() }
                        if (state.isLoading) {
                            disabled()
                        }
                    }) {
                        Text(if (state.isLoading) Strings.Auth.sendCodeLoading else Strings.Auth.sendCodeButton)
                    }
                } else {
                    // Resend code button
                    Button({
                        style {
                            property("padding", "10px 16px")
                            property("background-color", "#6c757d")
                            property("color", "white")
                            property("border", "none")
                            property("border-radius", "4px")
                            property("cursor", "pointer")
                            property("font-size", "14px")
                            property("margin-right", "8px")
                        }
                        onClick { handleSendCode() }
                        if (state.isLoading || countdown > 0) {
                            disabled()
                        }
                    }) {
                        Text(
                            buildString {
                                append(if (countdown > 0) {
                                    Strings.Auth.resendCountdown.replace("{0}", countdown.toString())
                                } else {
                                    Strings.Auth.resendButton
                                })
                            },
                        )
                    }

                    // Verify button for new users
                    Button({
                        style {
                            property("padding", "10px 16px")
                            property("background-color", "#28a745")
                            property("color", "white")
                            property("border", "none")
                            property("border-radius", "4px")
                            property("cursor", "pointer")
                            property("font-size", "14px")
                            property("margin-right", "8px")
                        }
                        onClick { handleVerify() }
                        if (state.isLoading) {
                            disabled()
                        }
                    }) {
                        Text(if (state.isLoading) Strings.Auth.verifyLoading else Strings.Auth.newRegistrationButton)
                    }

                    // Login button for existing users
                    Button({
                        classes(AppStyleSheet.submitButton)
                        onClick { handleLogin() }
                        if (state.isLoading) {
                            disabled()
                        }
                    }) {
                        Text(if (state.isLoading) Strings.Auth.loginLoading else Strings.Auth.loginButton)
                    }
                }
            }

            // Help text
            P({
                style {
                    property("font-size", "12px")
                    property("color", "#888")
                    property("margin-top", "16px")
                    property("text-align", "center")
                }
            }) {
                Text(Strings.Auth.loginDescription)
            }
        }
    }
}
